import { buildViewReadQuery } from '../chunk-Y2OLMRTF.js';
import '../chunk-WVLHT3DL.js';
import '../chunk-HYUFJAEW.js';
import '../chunk-KOK2EBEC.js';
import '../chunk-Q6TSJP63.js';
import '../chunk-O2TUCS6S.js';
import { normalizeAddress, coerceNumber } from '../chunk-N5C3INLG.js';
import '../chunk-AEYXO6NC.js';
import '../chunk-4RRJ2EBY.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/prediction/index.ts
var PREDICTION_VIEW_IDS = {
  /** One row per wallet: markets settled, won, P&L, cost, informed entries. */
  actors: "mv_prediction_actors",
  /** One row per (market, wallet) settled at resolution. */
  settlements: "mv_prediction_settlements",
  /** Net shares and cash per (wallet, market, outcome). */
  positions: "mv_prediction_positions",
  /** One row per fill. */
  trades: "mv_prediction_trades",
  /** One row per resolution, with the venue's question. */
  resolutions: "mv_prediction_resolutions"
};
var PREDICTION_FACETS = {
  settledTrader: "prediction_settled_trader",
  settledMarket: "prediction_settled_market",
  won: "prediction_settled_won",
  pnl: "prediction_settled_pnl",
  cost: "prediction_settled_cost",
  informed: "prediction_settled_informed",
  trader: "prediction_trader",
  market: "prediction_market",
  outcome: "prediction_outcome",
  side: "prediction_side",
  shares: "prediction_shares",
  usdc: "prediction_usdc",
  price: "prediction_price",
  resolvedMarket: "prediction_resolved_market",
  winningOutcome: "prediction_winning_outcome",
  question: "prediction_market_question",
  slug: "prediction_market_slug"
};
var INFORMED_ENTRY_SITUATION_ID = "prediction.informed_entry";
var DEFAULT_PREDICTION_LIMIT = 100;
var ORDER_COLUMN = {
  won: PREDICTION_FACETS.won,
  pnl: PREDICTION_FACETS.pnl,
  informed: PREDICTION_FACETS.informed,
  markets: "row_count"
};
function buildPredictionBoardQuery(order = "won", filters = {}) {
  return buildViewReadQuery(PREDICTION_VIEW_IDS.actors, {
    orderBy: ORDER_COLUMN[order],
    limit: filters.limit ?? DEFAULT_PREDICTION_LIMIT,
    scope: filters.scope
  });
}
function buildWalletPredictionTotalsQuery(wallet, filters = {}) {
  return buildViewReadQuery(PREDICTION_VIEW_IDS.actors, {
    filters: { [PREDICTION_FACETS.settledTrader]: normalizeAddress(wallet) },
    limit: 1,
    scope: filters.scope
  });
}
function buildWalletSettlementsQuery(wallet, filters = {}) {
  return buildViewReadQuery(PREDICTION_VIEW_IDS.settlements, {
    filters: { [PREDICTION_FACETS.settledTrader]: normalizeAddress(wallet) },
    orderBy: "block_timestamp",
    limit: filters.limit ?? 200,
    scope: filters.scope
  });
}
function buildMarketNamesQuery(marketIds, filters = {}) {
  return buildViewReadQuery(PREDICTION_VIEW_IDS.resolutions, {
    filters: { [PREDICTION_FACETS.resolvedMarket]: [...new Set(marketIds.map((m) => m.toLowerCase()))] },
    limit: Math.max(1, marketIds.length),
    scope: filters.scope
  });
}
function text(value) {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  return trimmed ? trimmed : void 0;
}
function wilsonLowerBound(wins, n, z = 1.96) {
  if (!(n > 0)) return 0;
  const p = Math.min(1, Math.max(0, wins / n));
  const z2 = z * z;
  const centre = p + z2 / (2 * n);
  const margin = z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n);
  return Math.max(0, (centre - margin) / (1 + z2 / n));
}
function overCountedReason(wallet) {
  return `${wallet.won} wins counted against ${wallet.markets} settled markets: some settlements were recorded twice, so this record cannot be scored until they are counted once`;
}
function predictionWalletsFromRows(rows) {
  const out = [];
  for (const row of rows) {
    const trader = text(row[PREDICTION_FACETS.settledTrader]);
    const wallet = trader ? normalizeAddress(trader) : void 0;
    if (!wallet) continue;
    const markets = coerceNumber(row.row_count) ?? 0;
    if (markets <= 0) continue;
    const won = coerceNumber(row[PREDICTION_FACETS.won]) ?? 0;
    const pnl = coerceNumber(row[PREDICTION_FACETS.pnl]);
    const cost = coerceNumber(row[PREDICTION_FACETS.cost]);
    const overCounted = won > markets;
    out.push({
      wallet,
      markets,
      won,
      ...overCounted ? { overCounted: true } : {},
      winRate: overCounted ? 0 : won / markets,
      winRateLow: overCounted ? 0 : wilsonLowerBound(won, markets),
      ...pnl !== void 0 ? { pnl } : {},
      ...cost !== void 0 ? { cost } : {},
      ...pnl !== void 0 && cost !== void 0 && cost > 0 ? { returnOnCost: pnl / cost } : {},
      informed: coerceNumber(row[PREDICTION_FACETS.informed]) ?? 0,
      ...coerceNumber(row.last_seen) !== void 0 ? { lastSettled: coerceNumber(row.last_seen) } : {}
    });
  }
  return out;
}
function walletSettlementsFromRows(rows) {
  const out = [];
  for (const row of rows) {
    const market = text(row[PREDICTION_FACETS.settledMarket])?.toLowerCase();
    if (!market) continue;
    const pnl = coerceNumber(row[PREDICTION_FACETS.pnl]);
    const cost = coerceNumber(row[PREDICTION_FACETS.cost]);
    const settledAt = coerceNumber(row.block_timestamp);
    out.push({
      market,
      won: (coerceNumber(row[PREDICTION_FACETS.won]) ?? 0) > 0,
      ...pnl !== void 0 ? { pnl } : {},
      ...cost !== void 0 ? { cost } : {},
      informed: (coerceNumber(row[PREDICTION_FACETS.informed]) ?? 0) > 0,
      ...text(row.tx_id) ? { txId: text(row.tx_id) } : {},
      ...settledAt !== void 0 ? { settledAt } : {}
    });
  }
  return out;
}
function marketNamesFromRows(rows) {
  const out = {};
  for (const row of rows) {
    const market = text(row[PREDICTION_FACETS.resolvedMarket])?.toLowerCase();
    if (!market) continue;
    const question = text(row[PREDICTION_FACETS.question]);
    const slug = text(row[PREDICTION_FACETS.slug]);
    out[market] = { ...out[market] ?? {}, ...question ? { question } : {}, ...slug ? { slug } : {} };
  }
  return out;
}
function rankPredictionWallets(wallets, { sort = "confidence", minMarkets = 1 } = {}) {
  const key = (w) => {
    switch (sort) {
      case "rate":
        return w.winRate;
      case "pnl":
        return w.pnl ?? Number.NEGATIVE_INFINITY;
      case "informed":
        return w.informed;
      case "markets":
        return w.markets;
      default:
        return w.winRateLow;
    }
  };
  return wallets.filter((w) => w.markets >= minMarkets).slice().sort((a, b) => key(b) - key(a) || b.markets - a.markets || a.wallet.localeCompare(b.wallet));
}
function polymarketUrl(slug) {
  return slug ? `https://polymarket.com/event/${encodeURIComponent(slug)}` : void 0;
}

export { DEFAULT_PREDICTION_LIMIT, INFORMED_ENTRY_SITUATION_ID, PREDICTION_FACETS, PREDICTION_VIEW_IDS, buildMarketNamesQuery, buildPredictionBoardQuery, buildWalletPredictionTotalsQuery, buildWalletSettlementsQuery, marketNamesFromRows, overCountedReason, polymarketUrl, predictionWalletsFromRows, rankPredictionWallets, walletSettlementsFromRows, wilsonLowerBound };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map